/**
 * Accessible Accordion (Vanilla JS)
 *
 * Features:
 *  - Click or press Enter/Space on headers to open/close sections
 *  - Smooth sliding animation using element height
 *  - Respects `prefers-reduced-motion`
 *  - Keyboard navigation: ArrowUp, ArrowDown, Home, End
 *  - Uses `aria-expanded` and `aria-hidden` for accessibility
 *  - Optional "only one open at a time" mode with `.module-accordion--single`
 *  - Prevents animation interruption: ignores clicks while animating
 *
 * Markup expectation:
 *  <div class="module-accordion" data-accordion>
 *    <div class="module-accordion__item">
 *      <button class="module-accordion__toggle" aria-controls="panel-1" aria-expanded="false">Title</button>
 *      <div id="panel-1" class="module-accordion__panel" role="region" aria-labelledby="..." aria-hidden="true">
 *        <div class="module-accordion__panel-inner">...</div>
 *      </div>
 *    </div>
 *  </div>
 */

// Configuration constants
const ACCORDION_SELECTOR = "[data-accordion]"; // selector for accordion blocks
const REDUCE             = window.matchMedia("(prefers-reduced-motion: reduce)").matches; // motion preference
const ANIMATION_DURATION = 300; // must match CSS transition duration

/**
 * Expand a panel with a smooth height animation.
 */
export function openPanel(item, btn, panel) {
	// Prevent multiple triggers while animating
	if (panel.dataset.animating === "true")
	{
		return;
	}

	panel.dataset.animating = "true";

	// Update ARIA and CSS state
	btn.setAttribute("aria-expanded", "true");
	panel.setAttribute("aria-hidden", "false");
	item.classList.add("module-accordion__item--open");

	// Toggle the icon state
	const toggleIcon = btn.querySelector('.tcon');
	toggleIcon?.classList.add('tcon-transform');

	// Panels are not focusable until animation finishes
	panel.inert = true;

	// Skip animation if user prefers reduced motion
	if (REDUCE)
	{
		panel.style.height = "auto";
		panel.inert        = false; // enable focus immediately in reduced-motion mode
		delete panel.dataset.animating;
		return;
	}

	// Measure inner content height
	const inner     = panel.querySelector(".module-accordion__panel-inner") || panel;
	const endHeight = inner.scrollHeight;

	// Start from 0 → expand to measured height
	panel.style.height = "0px";

	// Force browser to register starting height
	// eslint-disable-next-line no-unused-expressions
	panel.offsetHeight;

	// Animate to final height
	panel.style.transition = `height ${ANIMATION_DURATION}ms ease`;
	panel.style.height     = `${endHeight}px`;

	// After animation ends, set height:auto for natural resizing
	const tidy = () => {
		panel.style.transition = "";
		panel.style.height     = "auto";

		// Enable focusability ONLY AFTER animation completes
		panel.inert = false;

		delete panel.dataset.animating; // unlock after animation
		panel.removeEventListener("transitionend", tidy);
	};

	panel.addEventListener("transitionend", tidy);

	// Fallback: ensure cleanup runs even if transitionend is missed
	setTimeout(() => {
		if (panel.style.transition !== "")
		{
			tidy();
		}
	}, ANIMATION_DURATION + 20);
}

/**
 * Collapse a panel with smooth height animation.
 */
function closePanel(item, btn, panel) {

	// Prevent multiple triggers while animating
	if (panel.dataset.animating === "true")
	{
		return;
	}

	panel.dataset.animating = "true";

	btn.setAttribute("aria-expanded", "false");
	panel.setAttribute("aria-hidden", "true");
	item.classList.remove("module-accordion__item--open");

	// Toggle the icon state
	const toggleIcon = btn.querySelector('.tcon');
	toggleIcon?.classList.remove('tcon-transform');

	// Immediately disable focusable children when closing
	panel.inert = true;

	// Skip animation if reduced motion is enabled
	if (REDUCE)
	{
		panel.style.height = "0px";
		delete panel.dataset.animating;
		return;
	}

	const inner       = panel.querySelector(".module-accordion__panel-inner") || panel;
	const startHeight = inner.scrollHeight;

	// Set explicit start height → animate to 0
	panel.style.height = `${startHeight}px`;

	// Force reflow
	// eslint-disable-next-line no-unused-expressions
	panel.offsetHeight;
	panel.style.transition = `height ${ANIMATION_DURATION}ms ease`;
	panel.style.height     = "0px";

	// Clean up after transition completes
	const after = () => {
		panel.style.transition = "";
		panel.style.height     = "";
		delete panel.dataset.animating; // unlock after animation
		panel.removeEventListener("transitionend", after);
	};
	panel.addEventListener("transitionend", after);

	// Fallback cleanup (safety)
	setTimeout(() => {
		if (panel.style.transition !== "")
		{
			after();
		}
	}, ANIMATION_DURATION + 20);
}

/**
 * Toggle a single accordion item.
 * If `.module-accordion--single` is present, close others first.
 */
function toggleItem(item, btn, panel, singleMode) {

	// Ignore clicks while panel is animating
	if (panel.dataset.animating === "true")
	{
		return;
	}

	const isOpen = btn.getAttribute("aria-expanded") === "true";

	if (isOpen)
	{
		// Collapse if already open
		closePanel(item, btn, panel);
	}
	else
	{
		// If single-mode: close any other open items first
		if (singleMode)
		{
			const siblings = item.parentElement.querySelectorAll(".module-accordion__item--open");

			siblings.forEach((other) => {
				if (other === item)
				{
					return;
				}

				const ob = other.querySelector(".module-accordion__toggle");
				const op = other.querySelector(".module-accordion__panel");

				if (ob && op)
				{
					closePanel(other, ob, op);
				}
			});
		}
		openPanel(item, btn, panel);
	}
}

/**
 * Handle keyboard navigation between accordion buttons.
 * ArrowUp / ArrowDown / Home / End move focus logically.
 */
function onButtonKeydown(e, buttons) {

	const key   = e.key;
	const index = buttons.indexOf(e.currentTarget);

	if (["ArrowDown", "ArrowUp", "Home", "End"].includes(key))
	{
		e.preventDefault();

		let next = index;

		if (key === "ArrowDown")
		{
			next = (index + 1) % buttons.length;
		}

		if (key === "ArrowUp")
		{
			next = (index - 1 + buttons.length) % buttons.length;
		}

		if (key === "Home")
		{
			next = 0;
		}

		if (key === "End")
		{
			next = buttons.length - 1;
		}

		buttons[next].focus();
	}
}

/**
 * Initialize one accordion block.
 * Sets up click + keyboard listeners for all items.
 */
function initAccordion(root) {
	const items      = Array.from(root.querySelectorAll(".module-accordion__item"));
	const buttons    = items.map((i) => i.querySelector(".module-accordion__toggle"));
	const singleMode = root.classList.contains("module-accordion--single");

	buttons.forEach((btn, i) => {
		const item  = items[i];
		const panel = item.querySelector(".module-accordion__panel");

		if (!btn || !panel)
		{
			return;
		}

		// Ensure ARIA attributes are set
		if (!btn.hasAttribute("aria-expanded"))
		{
			btn.setAttribute("aria-expanded", "false");
		}

		if (!panel.hasAttribute("aria-hidden"))
		{
			panel.setAttribute("aria-hidden", "true");
		}

		// Set initial inert state based on open/closed state
		const isOpen = btn.getAttribute("aria-expanded") === "true";
		panel.inert  = !isOpen;

		// Toggle on click
		btn.addEventListener("click", () => toggleItem(item, btn, panel, singleMode));

		// Keyboard navigation
		btn.addEventListener("keydown", (e) => onButtonKeydown(e, buttons));
	});
}

/**
 * Auto-initialize all accordions
 */
document.querySelectorAll(ACCORDION_SELECTOR).forEach(initAccordion);